import React, { useState, useMemo, useRef, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faCopy, faTimes, faGripVertical, faStar as faStarSolid, faEdit } from '@fortawesome/free-solid-svg-icons'
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons'
import { getNamespaces, getScopedVars, GLOBAL_NAMESPACE, ComponentNamespace, publish, PublishedDict } from '../redux/pubsub'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui-primitives/accordion'

interface AppVariablesSidebarProps {
  isOpen: boolean
  onClose: () => void
  variables: PublishedDict
}

interface Position {
  x: number
  y: number
}

interface Size {
  width: number
  height: number
}

type ResizeHandle = 'bottom-right' | 'right' | 'bottom' | null

export function AppVariablesSidebar({ isOpen, onClose, variables }: AppVariablesSidebarProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [favoriteVariables, setFavoriteVariables] = useState<Set<string>>(new Set())
  const [position, setPosition] = useState<Position>({ x: window.innerWidth - 400, y: 0 })
  const [size, setSize] = useState<Size>({ width: 400, height: 600 })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState<ResizeHandle>(null)
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState<{ size: Size; mouse: Position }>({
    size: { width: 400, height: 600 },
    mouse: { x: 0, y: 0 }
  })
  const sidebarRef = useRef<HTMLDivElement>(null)

  const variableEntries = useMemo(() => Object.entries(variables), [variables])
  const namespaced = useMemo(() => {
    const namespaced: Record<string, [string, any][]> = {}
    for (const key of getNamespaces(variables)) {
      namespaced[key] = Object.entries(getScopedVars(variables, key)!) // `!` since `for (const key of getNamespaces(variables))`
    }
    return namespaced
  }, [variables])

  const filteredVariables = useMemo(() => {
    let filtered = variableEntries

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = variableEntries.filter(([key, value]) =>
        key.toLowerCase().includes(searchLower) ||
        String(value).toLowerCase().includes(searchLower)
      )
    }

    // Sort by favorites first, then alphabetically by name
    return filtered.sort(([aKey], [bKey]) => {
      const aIsFavorite = favoriteVariables.has(aKey)
      const bIsFavorite = favoriteVariables.has(bKey)

      // If one is favorite and other is not, favorite comes first
      if (aIsFavorite && !bIsFavorite) return -1
      if (!aIsFavorite && bIsFavorite) return 1

      // If both are favorites or both are not favorites, sort alphabetically
      return aKey.localeCompare(bKey)
    })
  }, [variableEntries, searchTerm, favoriteVariables])

  const toggleFavorite = useCallback((variableName: string) => {
    setFavoriteVariables(prev => {
      const newFavorites = new Set(prev)
      if (newFavorites.has(variableName)) {
        newFavorites.delete(variableName)
      } else {
        newFavorites.add(variableName)
      }
      return newFavorites
    })
  }, [])

  // Drag functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!sidebarRef.current) return

    const rect = sidebarRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    setIsDragging(true)
  }, [])

  // Resize functionality
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: ResizeHandle) => {
    e.stopPropagation() // Prevent dragging
    setIsResizing(handle)
    setResizeStart({
      size: { ...size },
      mouse: { x: e.clientX, y: e.clientY }
    })
  }, [size])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y

      // Constrain to viewport bounds
      const maxX = window.innerWidth - size.width
      const maxY = window.innerHeight - size.height

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      })
    } else if (isResizing) {
      const deltaX = e.clientX - resizeStart.mouse.x
      const deltaY = e.clientY - resizeStart.mouse.y

      let newWidth = resizeStart.size.width
      let newHeight = resizeStart.size.height

      if (isResizing === 'bottom-right' || isResizing === 'right') {
        newWidth = Math.max(280, Math.min(resizeStart.size.width + deltaX, window.innerWidth - position.x))
      }
      if (isResizing === 'bottom-right' || isResizing === 'bottom') {
        newHeight = Math.max(200, Math.min(resizeStart.size.height + deltaY, window.innerHeight - position.y))
      }

      setSize({ width: newWidth, height: newHeight })
    }
  }, [isDragging, isResizing, dragOffset, resizeStart, size.width, size.height, position.x, position.y])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setIsResizing(null)
  }, [])

  // Add global mouse event listeners
  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = 'none'

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.userSelect = ''
      }
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp])

  const favoriteCount = favoriteVariables.size

  return (
    <div
      ref={sidebarRef}
      className={`fixed bg-gray-800 border border-gray-600 z-[100] flex flex-col rounded-lg shadow-lg ${
        isDragging ? 'cursor-grabbing' : ''
      } ${isResizing ? 'select-none' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        minWidth: '280px',
        minHeight: '200px',
      }}
    >
      {/* Draggable Header */}
      <div
        className={`flex justify-between items-center px-4 py-1 border-b border-gray-600 bg-gray-700 rounded-t-lg ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faGripVertical} className="text-gray-400 text-sm" />
          <h3 className="text-lg font-semibold text-white select-none">
            App Variables
          </h3>
          {favoriteCount > 0 && (
            <span className="bg-yellow-600 text-yellow-100 text-xs px-2 py-1 rounded-full select-none">
              <FontAwesomeIcon icon={faStarSolid} className="mr-1" />
              {favoriteCount}
            </span>
          )}
        </div>
        <button
          className="bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded"
          onClick={onClose}
          aria-label="Close sidebar"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Search Container */}
      <div className="px-4 py-1 border-b border-gray-600 flex-shrink-0">
        <div className="flex items-center bg-gray-700 border border-gray-600 rounded px-3 py-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 text-sm mr-2"
          />
          <input
            type="text"
            placeholder="Search variables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none text-white text-sm placeholder-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-gray-400 hover:text-white p-1 rounded ml-2"
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-1 min-h-0 border-b border-gray-600">
        <Accordion className="text-white px-4 border-b border-gray-600"
          defaultValue="global"
          type="single" collapsible
        >
          {Object.entries(namespaced).map(([key, namespacedVariables]) => (
            <AccordionItem className="border-0" value={key} key={key}>
              <AccordionTrigger className="py-1 text-sm border-t rounded-none border-gray-600">namespace: {key}</AccordionTrigger>
              <AccordionContent>{namespacedVariables.map(([key, value], index) => (
                <AppVariable key={`${key}-${index}`}
                  name={key}
                  value={formatValue(value)}
                  isFavorite={favoriteVariables.has(key)}
                  toggleFavorite={() => toggleFavorite(key)}
                  namespace={key}
                />
              ))}</AccordionContent>
            </AccordionItem>
          ))}
          <AccordionItem value="global">
            <AccordionTrigger className="py-1 text-sm border-t rounded-none border-gray-600">global</AccordionTrigger>
            <AccordionContent>
            {filteredVariables.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                {searchTerm ? `No variables found matching "${searchTerm}"` : 'No variables found'}
              </div>
            ) : (
              filteredVariables.map(([key, value], index) => (
                  <AppVariable key={key}
                    name={key}
                    value={formatValue(value)}
                    isFavorite={favoriteVariables.has(key)}
                    toggleFavorite={() => toggleFavorite(key)}
                    namespace={GLOBAL_NAMESPACE}
                  />
                ))
              
            )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Resize Handles */}
      {/* Bottom-right corner resize handle */}
      <div
        className="absolute bottom-0 right-0 w-4 h-4 cursor-nw-resize bg-gray-600 hover:bg-gray-500 rounded-tl-lg"
        onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
        title="Resize"
      >
        <div className="absolute bottom-1 right-1 w-2 h-2">
          <div className="absolute bottom-0 right-0 w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="absolute bottom-0 right-2 w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="absolute bottom-2 right-0 w-1 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>

      {/* Right edge resize handle */}
      <div
        className="absolute top-12 right-0 w-2 h-16 cursor-ew-resize hover:bg-gray-600/50 rounded-l"
        onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
        title="Resize width"
      />

      {/* Bottom edge resize handle */}
      <div
        className="absolute bottom-0 left-4 right-4 h-2 cursor-ns-resize hover:bg-gray-600/50 rounded-t"
        onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
        title="Resize height"
      />
    </div>
  )
}

function AppVariable(props: {
  name: string
  value: string
  isFavorite: boolean
  toggleFavorite: () => void
  namespace: ComponentNamespace
}) {
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  const copyToClipboard = async (text: string, type: 'name' | 'value', variableName: string) => {
    try {
      await navigator.clipboard.writeText(text)
      const itemKey = `${variableName}-${type}`
      setCopiedItem(itemKey)

      setTimeout(() => {
        setCopiedItem(prev => prev === itemKey ? null : prev)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      alert(`Copy failed due to browser compatibility. Please manually copy: ${text}`)
    }
  }

  return (
    <div
      className={`border rounded px-2 py-1 transition-all duration-200 ${
        props.isFavorite
          ? 'bg-yellow-50 border-yellow-300 shadow-sm'
          : 'bg-white border-gray-300'
      }`}
    >
      {/* Variable Name with Favorite Button */}
      <div className="flex justify-between items-center gap-1">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            className={`p-1 rounded transition-colors duration-200 ${
              props.isFavorite
                ? 'text-yellow-600 hover:text-yellow-700'
                : 'text-gray-400 hover:text-yellow-600'
            }`}
            onClick={props.toggleFavorite}
            title={props.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FontAwesomeIcon
              icon={props.isFavorite ? faStarSolid : faStarOutline}
              className="text-sm"
            />
          </button>
          <span className={`font-semibold text-sm break-all ${
            props.isFavorite ? 'text-gray-900' : 'text-gray-800'
          }`}>
            {props.name}
          </span>
        </div>
        <button
          className={`px-2 py-1 rounded text-xs flex-shrink-0 ${
            copiedItem === `${props.name}-name`
              ? 'bg-green-200 text-green-800 border border-green-300'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-300'
          }`}
          onClick={() => copyToClipboard(props.name, 'name', props.name)}
          title="Copy variable name"
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      </div>

      {/* Variable Value */}
      <pre className={`flex justify-between font-mono text-xs px-2 py-1 rounded border whitespace-pre-wrap break-all max-h-20 overflow-y-auto ${
        props.isFavorite
          ? 'text-gray-800 bg-yellow-100 border-yellow-200'
          : 'text-gray-800 bg-gray-100 border-gray-300'
      }`}>
        {props.value}
        <div className="flex justify-between gap-2 items-center">
          <button title="Edit value"
            className="transition active:scale-75"
            onClick={() => {
              const newValue = prompt('value to set')
              if (newValue == null) return
              publish(props.name, newValue, props.namespace, 'set directly via app-variables debugger')
            }}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button title="Copy variable value"
            className="transition active:scale-75"
            onClick={() => copyToClipboard(props.value, 'value', props.name)}
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>
      </pre>
    </div>
  )
}

function formatValue(value: any): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}
